import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Download, X, Loader2, FileWarning } from 'lucide-react';

const PREVIEWABLE = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']);

function getExtension(filename) {
  if (!filename) return '';
  return filename.split('.').pop()?.toLowerCase() || '';
}

export default function PreviewModal({ open, onClose, fileUrl, filename }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const ext = getExtension(filename);
  const canPreview = PREVIEWABLE.has(ext);
  const isPdf = ext === 'pdf';
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext);

  const handleDownload = async () => {
    if (!fileUrl) return;
    setDownloading(true);
    try {
      // Fetch as blob → guaranteed download, no browser preview
      const resp = await fetch(fileUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'document';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(objectUrl); }, 200);
    } catch {
      // silent fail
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setLoading(true); setError(false); onClose(); } }}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0" data-testid="preview-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-base font-semibold truncate" data-testid="preview-modal-title">
              {filename || 'File Preview'}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden bg-slate-50 relative" data-testid="preview-modal-body">
          {canPreview && !error ? (
            <>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-50">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              )}
              {isImage ? (
                <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
                  <img
                    src={fileUrl}
                    alt={filename}
                    className="max-w-full max-h-full object-contain"
                    onLoad={() => setLoading(false)}
                    onError={() => { setLoading(false); setError(true); }}
                    data-testid="preview-modal-image"
                  />
                </div>
              ) : isPdf ? (
                <object
                  data={fileUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  onLoad={() => setLoading(false)}
                  data-testid="preview-modal-pdf"
                >
                  <div className="h-full flex flex-col items-center justify-center gap-4 p-8">
                    <FileWarning className="h-16 w-16 text-slate-300" />
                    <p className="text-lg font-medium text-slate-700">PDF preview unavailable in this browser.</p>
                    <Button onClick={handleDownload} disabled={downloading} data-testid="preview-download-fallback">
                      <Download className="h-4 w-4 mr-2" />
                      {downloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </div>
                </object>
              ) : (
                <iframe
                  src={fileUrl}
                  title={filename}
                  className="w-full h-full border-0"
                  onLoad={() => setLoading(false)}
                  onError={() => { setLoading(false); setError(true); }}
                  data-testid="preview-modal-iframe"
                />
              )}
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-4 p-8" data-testid="preview-unsupported">
              <FileWarning className="h-16 w-16 text-slate-300" />
              <p className="text-lg font-medium text-slate-700">
                {error ? 'Failed to load file preview' : 'This file type cannot be previewed.'}
              </p>
              <p className="text-sm text-slate-500">
                {error
                  ? 'There was an error loading the file. Try downloading it instead.'
                  : `Files with .${ext || 'unknown'} extension are not supported for inline preview.`}
              </p>
              <Button onClick={handleDownload} disabled={downloading} data-testid="preview-download-fallback">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Downloading...' : 'Download File'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t shrink-0">
          <div className="flex gap-2 w-full justify-end">
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading} data-testid="preview-download-btn">
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
            <Button variant="outline" size="sm" onClick={onClose} data-testid="preview-close-btn">
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
